import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, User2 } from "lucide-react";
import { UserDetails } from "@/types/userDetails";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";

interface ListingDetailsSellerProps {
  seller: UserDetails; // User object
}

export const ListingDetailsSeller: React.FC<ListingDetailsSellerProps> = ({
  seller,
}) => {
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};

  if (!seller) {
    return <div>{L.noSellerAvailable ?? "Ingen säljare tillgänglig"}</div>;
  }

  const isSellerLoading = false;
  const sellerError = null;

  if (isSellerLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">
          {L.loadingSellerInfo ?? "Laddar säljarinformation..."}
        </span>
      </div>
    );
  }

  if (sellerError) {
    return (
      <p className="text-muted-foreground">
        Något gick fel. {sellerError.message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {/* Seller Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User2 className="h-5 w-5" />
            {L.sellerInfo ?? "Säljarinformation"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="font-medium text-blue-500">{L.name ?? "Namn"}</p>
                <p className="text-sm">
                  {seller.firstName} {seller.lastName}
                </p>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-blue-500">
                  {L.email ?? "E-post"}
                </p>
                <p className="text-sm">{seller.email}</p>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-blue-500">
                  {L.phone ?? "Telefon"}
                </p>
                <p className="text-sm">{seller.phoneNumber}</p>
              </div>
              <div className="space-y-2">
                <p className="font-medium text-blue-500">{L.type ?? "Typ"}</p>
                <Badge variant="default">
                  {seller.role === "dealer"
                    ? (L.dealer ?? "Återförsäljare")
                    : seller.role === "business"
                      ? (L.business ?? "Företagskund")
                      : (L.private ?? "Privatperson")}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
